import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { LegacyTokens } from "../../../../theme/b13Tokens";
import { B13 } from "../../../../theme/b13Tokens";

// Three physical stops, two semantic states, FOUR visual states:
//   LEFT   + unsuccessful  → red/rose        ("unsuccessful")
//   CENTER + successful    → neutral slate   ("neutral-success", CANONICAL)
//   RIGHT  + successful    → green/mint      ("successful-green")
//   CENTER + not_counted   → dashed slate    ("legacy-not-counted")
//
// outcomeState is the only persisted field. On save→reopen, successful
// hydrates at CENTER (neutral slate) regardless of whether the user last
// left it at CENTER or RIGHT — a physical position field is intentionally
// NOT added to the schema.
//
// Legacy "not_counted" rows are visually distinguished by a dashed border
// and a "(legacy)" label so a clinician never confuses them with an
// active CENTER Successful selection.

export type OutcomeState = "unsuccessful" | "not_counted" | "successful";

export type OutcomeStateToggleProps = {
  value: OutcomeState;
  onChange: (next: OutcomeState) => void;
  disabled?: boolean;
  labelId?: string;
};

const TRACK_W = 96;
const TRACK_H = 30;
const KNOB    = 24;

// Slate palette for the neutral-success CENTER state. Deliberately
// separate from LegacyTokens so the "not green" contract is enforced at
// the source rather than by leaning on a theme value that could shift.
const SLATE = {
  track:  "#E6EBF0",
  border: "#CBD5DF",
  ink:    "#64748B",
  label:  "#5F6F80",
};

type VisualState = "unsuccessful" | "neutral-success" | "successful-green" | "legacy-not-counted";

function stopFromValue(v: OutcomeState): 0 | 1 | 2 {
  if (v === "unsuccessful") return 0;
  return 1;
}

function visualStateFor(value: OutcomeState, stop: 0 | 1 | 2): VisualState {
  if (value === "unsuccessful") return "unsuccessful";
  if (value === "not_counted")  return "legacy-not-counted";
  return stop === 2 ? "successful-green" : "neutral-success";
}

export default function OutcomeStateToggle({
  value,
  onChange,
  disabled = false,
  labelId,
}: OutcomeStateToggleProps) {
  const [stop, setStop] = React.useState<0 | 1 | 2>(() => stopFromValue(value));
  const lastValueRef = React.useRef<OutcomeState>(value);
  React.useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setStop(stopFromValue(value));
    }
  }, [value]);

  const visual = visualStateFor(value, stop);

  // Style tokens keyed off visual state so CENTER-successful and
  // RIGHT-successful are guaranteed non-identical.
  const style = (() => {
    switch (visual) {
      case "unsuccessful":
        return {
          trackBg:     LegacyTokens.red,
          trackBorder: LegacyTokens.red,
          borderStyle: "solid" as const,
          knobIconColor: LegacyTokens.red,
          labelColor:  LegacyTokens.red,
          labelWeight: 600,
        };
      case "successful-green":
        return {
          trackBg:     LegacyTokens.emerald,
          trackBorder: LegacyTokens.emerald,
          borderStyle: "solid" as const,
          knobIconColor: LegacyTokens.emerald,
          labelColor:  LegacyTokens.emerald,
          labelWeight: 600,
        };
      case "neutral-success":
        return {
          trackBg:     SLATE.track,
          trackBorder: SLATE.border,
          borderStyle: "solid" as const,
          knobIconColor: SLATE.ink,
          labelColor:  SLATE.label,
          labelWeight: 500,
        };
      case "legacy-not-counted":
        return {
          trackBg:     SLATE.track,
          trackBorder: SLATE.border,
          borderStyle: "dashed" as const,
          knobIconColor: SLATE.ink,
          labelColor:  LegacyTokens.ink3,
          labelWeight: 500,
        };
    }
  })();

  const usable = TRACK_W - KNOB - 6;
  const zoneX = (idx: 0 | 1 | 2) => 3 + (usable / 2) * idx;
  const knobLeft = zoneX(stop);

  const setByStop = (i: 0 | 1 | 2) => {
    if (disabled) return;
    const next: OutcomeState = i === 0 ? "unsuccessful" : "successful";
    setStop(i);
    if (next !== value) {
      lastValueRef.current = next;
      onChange(next);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "ArrowLeft")  { e.preventDefault(); setByStop(Math.max(0, stop - 1) as 0 | 1 | 2); }
    if (e.key === "ArrowRight") { e.preventDefault(); setByStop(Math.min(2, stop + 1) as 0 | 1 | 2); }
    if (e.key === "Home")       { e.preventDefault(); setByStop(0); }
    if (e.key === "End")        { e.preventDefault(); setByStop(2); }
  };

  const ariaCheckedForZone = (i: 0 | 1 | 2): boolean => {
    if (i === 0) return value === "unsuccessful";
    // Zone 1 owns the aria-checked bit for successful (and legacy
    // not_counted). Zone 2 is a click alias for successful; its
    // aria-checked mirrors zone 1 so screen readers report a single
    // Successful radio, not two competing radios.
    return value === "successful" || value === "not_counted";
  };

  const zoneAriaLabel = (i: 0 | 1 | 2): string =>
    i === 0 ? "Unsuccessful outcome" : "Successful outcome";

  const labelText =
    value === "unsuccessful" ? "Unsuccessful outcome"
    : value === "not_counted" ? "Not counted (legacy)"
    : "Successful outcome";

  const showCheck = value === "successful";
  const showClose = value === "unsuccessful";

  return (
    <Box
      data-outcome={value}
      data-position={stop === 0 ? "left" : stop === 1 ? "center" : "right"}
      data-visual-state={visual}
      sx={{ display: "inline-flex", alignItems: "center", gap: 1, opacity: disabled ? 0.5 : 1 }}
    >
      <Box
        role="radiogroup"
        aria-labelledby={labelId}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        sx={{
          position: "relative",
          width: TRACK_W,
          height: TRACK_H,
          borderRadius: TRACK_H / 2,
          backgroundColor: style.trackBg,
          border: `1px ${style.borderStyle} ${style.trackBorder}`,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background-color 160ms, border-color 160ms",
          "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
        }}
      >
        {([0, 1, 2] as const).map((i) => (
          <Box
            key={i}
            role="radio"
            aria-checked={ariaCheckedForZone(i)}
            aria-label={zoneAriaLabel(i)}
            data-zone={i}
            onClick={(e) => { e.stopPropagation(); setByStop(i); }}
            sx={{
              position: "absolute",
              top: 0,
              left: `${(i * 100) / 3}%`,
              width: `${100 / 3}%`,
              height: "100%",
              zIndex: 2,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          />
        ))}

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: (TRACK_H - KNOB) / 2,
            left: knobLeft,
            width: KNOB,
            height: KNOB,
            borderRadius: "50%",
            backgroundColor: LegacyTokens.surface,
            boxShadow: "0 1px 3px rgba(0,0,0,0.20)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "left 180ms, background-color 160ms, color 160ms",
            color: style.knobIconColor,
            zIndex: 3,
            pointerEvents: "none",
          }}
        >
          {showCheck && <CheckIcon sx={{ fontSize: 16 }} />}
          {showClose && <CloseIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>

      <Typography
        id={labelId}
        sx={{
          fontSize: 13,
          fontWeight: style.labelWeight,
          color: style.labelColor,
          transition: "color 160ms",
        }}
      >
        {labelText}
      </Typography>
    </Box>
  );
}
